import { useEffect, useRef } from "react";
import Intercom from "@intercom/messenger-js-sdk";
import { useAuth } from "@/hooks/useAuth";

const APP_ID = "m4ns4aqy";

export function IntercomBoot() {
  const { user, loading } = useAuth();
  const bootedRef = useRef(false);
  const lastUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (loading) return;
    const w = window as any;

    if (user) {
      const createdAtSec = user.created_at
        ? Math.floor(new Date(user.created_at).getTime() / 1000)
        : undefined;
      const name =
        (user.user_metadata as any)?.full_name ||
        (user.user_metadata as any)?.name ||
        undefined;

      // If already booted as a different (or anon) user, shutdown first to clear session
      if (bootedRef.current && lastUserIdRef.current !== user.id) {
        try { w.Intercom?.("shutdown"); } catch {}
        bootedRef.current = false;
      }

      if (!bootedRef.current) {
        Intercom({
          app_id: APP_ID,
          user_id: user.id,
          email: user.email,
          name,
          created_at: createdAtSec,
        });
        bootedRef.current = true;
        lastUserIdRef.current = user.id;
      } else {
        try { w.Intercom?.("update", { email: user.email, name }); } catch {}
      }
    } else {
      // Logged out: ensure we're not still showing a prior identified session
      if (bootedRef.current && lastUserIdRef.current) {
        try { w.Intercom?.("shutdown"); } catch {}
        bootedRef.current = false;
        lastUserIdRef.current = null;
      }
      if (!bootedRef.current) {
        Intercom({ app_id: APP_ID });
        bootedRef.current = true;
      }
    }
  }, [user, loading]);

  // Post-boot health check — log a single warning if Intercom didn't render
  // a launcher within 4s. Common causes: ad-blocker, invalid app_id, or
  // workspace setting "Show Messenger to" excluding Visitors.
  useEffect(() => {
    if (loading) return;
    const t = window.setTimeout(() => {
      const w = window as any;
      const launcher = document.querySelector(
        ".intercom-lightweight-app, .intercom-launcher, .intercom-launcher-frame, [class*='intercom']"
      );
      if (!w.Intercom) {
        console.warn("[Intercom] SDK not present on window — script likely blocked (ad-blocker / CSP).");
      } else if (!launcher) {
        console.warn(
          "[Intercom] launcher not detected — likely blocked by extension, or workspace 'Show Messenger to' excludes Visitors."
        );
      }
    }, 4000);
    return () => window.clearTimeout(t);
  }, [loading]);

  return null;
}

export default IntercomBoot;
